/**
 * Cross-org UI-leak prevention (ADR-0010 Phase 2 — the #1 risk of the org
 * switcher). The server-side switch is already leak-free (the re-minted
 * session JWT re-resolves roles from the TARGET org's membership row), but
 * the web app must independently guarantee that org A's already-fetched
 * data can never render for even a frame under org B's session.
 *
 * This app has no client data-cache layer (no React Query/SWR/global
 * store) — every page fetches into local component `useState` on mount. That
 * means the only way to GUARANTEE full discard is to blow away the entire
 * in-memory JS heap and force every component to remount and refetch under
 * the new session cookie, rather than trying to enumerate and reset each
 * page's state individually (which would silently miss a future page that
 * forgets to opt in). A hard navigation does exactly that.
 *
 * `useOrgSwitch` (`hooks/useOrgSwitch.ts`) calls this immediately after a
 * successful `POST /api/session/switch-org`, before the caller does anything
 * else — it is the decisive step, not an optional extra.
 */
export interface DiscardOrgCacheOptions {
  /** Injectable for tests. Defaults to a hard navigation to `/dashboard`. */
  navigate?: (path: string) => void;
}

function defaultNavigate(path: string): void {
  window.location.href = path;
}

/**
 * Clears any storage that could carry org-scoped state across the switch,
 * then hard-navigates to `/dashboard` (never a client-side router
 * transition) so no mounted component or module-level variable survives
 * into the new org's session.
 *
 * `sessionStorage` is cleared as defense in depth: nothing org-scoped is
 * written there today, but a future feature that caches into it must not
 * leak across orgs either, and this call site is the single place that
 * enforces that invariant.
 */
export function discardOrgScopedCacheAndNavigate(options: DiscardOrgCacheOptions = {}): void {
  try {
    window.sessionStorage.clear();
  } catch {
    // sessionStorage can be unavailable in locked-down browsers — non-fatal,
    // the hard navigation below is what actually guarantees the discard.
  }

  const navigate = options.navigate ?? defaultNavigate;
  navigate('/dashboard');
}
